import { useLocation } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";
import {
  ShowcaseItem,
  type ShowcaseItemProps,
} from "~/components/3_organisms/ShowcaseItem";

export interface AlternatingShowcaseProps {
  /**
   * Array of showcase items to display
   */
  items: ShowcaseItemProps[];

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Detects if the current device is mobile based on screen width
 * Uses consistent breakpoints: mobile < 1280px, desktop >= 1280px
 * Follows the same pattern as other Tennis Gourmet components
 */
function getDeviceType() {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 1280 ? "mobile" : "desktop";
}

export function AlternatingShowcase({ items, className = "" }: AlternatingShowcaseProps) {
  const { ref } = useViewportVisibilityForAnimations();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(() => {
    // Initialize with hash-based index if available
    if (typeof window !== "undefined" && window.location.hash && items.length > 1) {
      const targetId = window.location.hash.substring(1);
      const targetIndex = items.findIndex((item) => item.id === targetId);
      if (targetIndex !== -1) {
        const extendedIndex = targetIndex + 1;
        return extendedIndex;
      }
    }
    return 1; // Start at 1 because of duplicate at beginning
  });

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(() => getDeviceType() === "mobile");
  const trackRef = useRef<HTMLDivElement>(null);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);

  // Touch/swipe state management
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number; time: number } | null>(
    null,
  );

  // Swipe configuration
  const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity (pixels per ms)
  const MAX_VERTICAL_DRIFT = 100; // Maximum vertical movement to still consider horizontal swipe

  // Create extended items array for infinite scrolling
  // Add last item at beginning and first item at end for seamless looping
  const extendedItems =
    items.length > 1
      ? [
          items[items.length - 1], // Last item at beginning
          ...items, // Original items
          items[0], // First item at end
        ]
      : items;

  // Check device type on resize
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = getDeviceType() === "mobile";
      setIsMobile(newIsMobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle hash fragment navigation
  useEffect(() => {
    if (!location.hash || items.length <= 1) {
      return;
    }

    const targetId = location.hash.substring(1);
    const targetIndex = items.findIndex((item) => item.id === targetId);

    if (targetIndex === -1) {
      return;
    }

    // Use setTimeout to avoid direct setState in useEffect
    const timeoutId = setTimeout(() => {
      if (isMobile) {
        // Mobile: Handle carousel navigation
        const extendedIndex = targetIndex + 1;
        setCurrentIndex(extendedIndex);
      } else {
        // Desktop: Use browser native scrolling to the element
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 0);

    // Also run after a delay to handle timing issues
    const delayedTimeoutId = setTimeout(() => {
      if (isMobile) {
        const extendedIndex = targetIndex + 1;
        setCurrentIndex(extendedIndex);
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(delayedTimeoutId);
    };
  }, [location.hash, isMobile, items]);

  // Handle carousel navigation with infinite scrolling
  const handleNext = () => {
    if (isTransitioning || !isMobile || items.length <= 1) return;

    setIsTransitioning(true);
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);

    // If we've reached the duplicate at the end, reset to the real first item
    if (nextIndex === extendedItems.length - 1) {
      setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = "none";
          setCurrentIndex(1); // Reset to real first item
          setTimeout(() => {
            if (trackRef.current) {
              trackRef.current.style.transition = "";
            }
          }, 50);
        }
      }, 500);
    }

    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const handlePrevious = () => {
    if (isTransitioning || !isMobile || items.length <= 1) return;

    setIsTransitioning(true);
    const prevIndex = currentIndex - 1;
    setCurrentIndex(prevIndex);

    // If we've reached the duplicate at the beginning, reset to the real last item
    if (prevIndex === 0) {
      setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = "none";
          setCurrentIndex(extendedItems.length - 2); // Reset to real last item
          setTimeout(() => {
            if (trackRef.current) {
              trackRef.current.style.transition = "";
            }
          }, 50);
        }
      }, 500);
    }

    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  // Touch event handlers for swipe detection
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || items.length <= 1 || isTransitioning) return;

    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    });
    setTouchEnd(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !isMobile || items.length <= 1 || isTransitioning) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touchStart.x - touch.clientX);
    const deltaY = Math.abs(touchStart.y - touch.clientY);

    // If horizontal movement is greater than vertical, prevent default scrolling
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }

    setTouchEnd({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || !isMobile || items.length <= 1 || isTransitioning)
      return;

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    const deltaTime = touchEnd.time - touchStart.time;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Check if this is a valid horizontal swipe
    const isHorizontalSwipe =
      Math.abs(deltaX) > SWIPE_THRESHOLD &&
      deltaY < MAX_VERTICAL_DRIFT &&
      velocity > SWIPE_VELOCITY_THRESHOLD;

    if (isHorizontalSwipe) {
      // Prevent default touch behaviors during swipe
      if (carouselWrapperRef.current) {
        carouselWrapperRef.current.style.touchAction = "none";
      }

      if (deltaX > 0) {
        // Swiped left (finger moved left) - go to next item
        handleNext();
      } else {
        // Swiped right (finger moved right) - go to previous item
        handlePrevious();
      }

      // Reset touch action after swipe
      setTimeout(() => {
        if (carouselWrapperRef.current) {
          carouselWrapperRef.current.style.touchAction = "";
        }
      }, 100);
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Get the actual current item index for dot pagination (accounting for duplicates)
  const getActualIndex = () => {
    if (currentIndex === 0) return items.length - 1; // Duplicate at beginning
    if (currentIndex === extendedItems.length - 1) return 0; // Duplicate at end
    return currentIndex - 1; // Adjust for duplicate at beginning
  };

  // Desktop: Show all items in alternating layout
  if (!isMobile) {
    return (
      <section ref={ref} className={`tg-alternating-showcase ${className}`}>
        {items.map((item, index) => (
          <div key={`showcase-${item.title}`}>
            <ShowcaseItem
              {...item}
              index={index}
              imagePosition={item.imagePosition || "auto"}
            />
          </div>
        ))}
      </section>
    );
  }

  // Mobile: Show carousel if multiple items, single item otherwise
  if (items.length <= 1) {
    return (
      <section ref={ref} className={`tg-alternating-showcase ${className}`}>
        <ShowcaseItem {...items[0]} index={0} />
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className={`tg-alternating-showcase tg-alternating-showcase--carousel ${className}`}
    >
      {/* Carousel Container */}
      <div
        ref={carouselWrapperRef}
        className="tg-alternating-showcase__carousel-wrapper"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }} // Allow vertical scrolling but handle horizontal swipes
      >
        <div
          ref={trackRef}
          className={`tg-alternating-showcase__carousel-track ${
            isTransitioning
              ? "tg-alternating-showcase__carousel-track--transitioning"
              : ""
          }`}
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {extendedItems.map((item, index) => {
            // Create unique key that accounts for duplicates
            const keyPrefix =
              index === 0
                ? "duplicate-last"
                : index === extendedItems.length - 1
                  ? "duplicate-first"
                  : "original";
            return (
              <div
                key={`carousel-${keyPrefix}-${item.title}`}
                className="tg-alternating-showcase__carousel-item"
              >
                <ShowcaseItem {...item} index={index} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Controls */}
      <button
        onClick={handlePrevious}
        disabled={isTransitioning}
        className={`tg-alternating-showcase__nav tg-alternating-showcase__nav--prev ${
          isTransitioning ? "tg-alternating-showcase__nav--disabled" : ""
        }`}
        aria-label="Previous showcase item"
        type="button"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        onClick={handleNext}
        disabled={isTransitioning}
        className={`tg-alternating-showcase__nav tg-alternating-showcase__nav--next ${
          isTransitioning ? "tg-alternating-showcase__nav--disabled" : ""
        }`}
        aria-label="Next showcase item"
        type="button"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dot Pagination */}
      <div className="tg-alternating-showcase__dots">
        {items.map((item, index) => (
          <div
            key={`dot-${item.title}`}
            className={`tg-alternating-showcase__dot ${
              index === getActualIndex() ? "tg-alternating-showcase__dot--active" : ""
            }`}
          />
        ))}
      </div>
    </section>
  );
}
